import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Empty, Spin } from 'antd';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useDataPortalStore } from '../../../store/dataPortalStore';
import { pickPageName } from '../../../utils/dataPortalCache';
import { DATA_PORTAL_EMBED_URL } from '../graphql/queries';

/** 超过这个时间还没触发 onLoad，就认为加载有问题，给用户兜底提示 */
const LOAD_TIMEOUT_MS = 10000;

/**
 * 商分门户页面的 iframe 承载页。
 *
 * 嵌入 URL **按需单条取**（`dataPortalEmbedUrl(pageKey)`），不再随菜单整批下发：
 * 它是免登凭证，整批下发会让几百个看板的菜单响应大一个数量级，且所有分享链接都落进 localStorage。
 * 后端取 URL 时仍会校验可见性，所以猜到 pageKey 也拿不到别人的看板。
 *
 * 跨源限制下我们**读不到 iframe 内部状态**——它是正常渲染了、还是被拒了，外层一概不知道。
 * 所以这里不做「智能判断」，只提供「重新加载」和超时兜底提示。
 *
 * **不提供「在新窗口打开」**：门户的嵌入型分享链接会检查 `Sec-Fetch-Dest: document`
 * 主动拒绝顶级直开（链接被转发出去就失效，这是它的安全设计），新窗口点开只会看到
 * 「此页面仅支持在指定系统内嵌入查看」，给按钮反而误导用户。
 *
 * 同理**不需要「登录门户」引导**：嵌入型分享免口令免登录，会话由链接本身换取。
 */
const EmbedPage: React.FC = () => {
    const { pageKey } = useParams<{ pageKey: string }>();
    const { t, i18n } = useTranslation();
    const pages = useDataPortalStore((s) => s.pages);
    const menuLoading = useDataPortalStore((s) => s.isLoading);

    const page = useMemo(() => pages.find((p) => p.pageKey === pageKey), [pages, pageKey]);

    const [loaded, setLoaded] = useState(false);
    const [timedOut, setTimedOut] = useState(false);
    // 换 key 强制 iframe 重新挂载，用于手动刷新
    const [reloadToken, setReloadToken] = useState(0);

    // 菜单里没有这个看板就不必问后端要 URL（后端也会拒），少一次必然失败的请求
    const { data, loading: urlLoading } = useQuery<{ dataPortalEmbedUrl: string | null }>(
        DATA_PORTAL_EMBED_URL,
        {
            variables: { pageKey },
            skip: !page,
            fetchPolicy: 'network-only',
            onError: (e) => console.warn('Fetch data portal embed url failed', e),
        },
    );
    const embedUrl = data?.dataPortalEmbedUrl ?? null;

    // 切换看板或手动刷新时重置加载状态
    useEffect(() => {
        setLoaded(false);
        setTimedOut(false);
    }, [pageKey, reloadToken]);

    useEffect(() => {
        if (!embedUrl || loaded) return;
        const timer = window.setTimeout(() => setTimedOut(true), LOAD_TIMEOUT_MS);
        return () => window.clearTimeout(timer);
    }, [embedUrl, loaded, pageKey, reloadToken]);

    if ((menuLoading && !page) || (page && urlLoading)) {
        return (
            <div className="flex-1 h-full flex items-center justify-center">
                <Spin />
            </div>
        );
    }

    // 后端不会返回无权限的看板，也不会给出它的 URL，所以「查不到」等价于「没权限或不存在」
    if (!page || !embedUrl) {
        return (
            <div className="flex-1 h-full flex items-center justify-center">
                <Empty description={t('pages.dataPortal.pageNotFoundOrNoAccess')} />
            </div>
        );
    }

    const title = pickPageName(page, i18n.language);

    return (
        <div className="flex-1 h-full flex flex-col min-h-0">
            {/* 不重复展示看板名称：左侧二级栏已经标出当前是哪个看板，
                而门户页面自己也有标题，再显示一遍就是三份同样的信息 */}
            <div className="flex items-center justify-end mb-2">
                <Button size="middle" onClick={() => setReloadToken((v) => v + 1)}>
                    <span className="font-bold">{t('pages.dataPortal.reload')}</span>
                </Button>
            </div>

            {timedOut && !loaded && (
                <div className="mb-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <span className="text-gray-600">{t('pages.dataPortal.loadTimeoutTip')}</span>
                </div>
            )}

            <iframe
                key={`${pageKey}-${reloadToken}`}
                title={title}
                src={embedUrl}
                onLoad={() => setLoaded(true)}
                className="flex-1 w-full min-h-0 border-0"
                referrerPolicy="strict-origin-when-cross-origin"
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-downloads allow-modals"
            />
        </div>
    );
};

export default EmbedPage;
